import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Button from '../ui/Button';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import { ForwardedStatPills } from './ForwardedOnboardingShared';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';

interface ForwardedDashboardCardProps {
    importCount: number;
    importedPaperCount: number;
    onSetUpTopic: () => void;
    onOpenInbox: () => void;
}

export default function ForwardedDashboardCard({
    importCount,
    importedPaperCount,
    onSetUpTopic,
    onOpenInbox,
}: ForwardedDashboardCardProps) {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={styles.card} testID="forwarded-dashboard-card">
            <View style={styles.eyebrow}>
                <Text style={[styles.eyebrowText, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardeddashboardcard.sourceConnected" comment="UI copy in components/onboarding/ForwardedDashboardCard.tsx">
                    Source connected
                </Trans></Text>
            </View>
            <Text style={[styles.title, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardeddashboardcard.yourSourceIsConnected" comment="UI copy in components/onboarding/ForwardedDashboardCard.tsx">
                Your source is connected
            </Trans></Text>
            <Text style={[styles.body, fontsLoaded && interRegular]}><Trans id="onboarding.forwardeddashboardcard.yourForwardedPapersAreReadySetUpAResearchTopic" comment="UI copy in components/onboarding/ForwardedDashboardCard.tsx">
                Your forwarded papers are ready. Set up a research topic so Paperzilla can add them to it, keep the original emails visible in Inbox, and filter future papers from all sources into one focused feed.
            </Trans></Text>
            <ForwardedStatPills
                importCount={importCount}
                importedPaperCount={importedPaperCount}
            />
            <View style={styles.actions}>
                <Button
                    label={msg({ id: 'forwardedDashboard.setUpTopic', message: 'Set up topic' })}
                    onPress={onSetUpTopic}
                    testID="forwarded-dashboard-setup-topic"
                />
                <Button
                    label={msg({ id: 'forwardedDashboard.openInbox', message: 'Open Inbox' })}
                    variant="secondary"
                    onPress={onOpenInbox}
                    testID="forwarded-dashboard-open-inbox"
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: '#ffffff',
        borderColor: '#dbe6da',
        borderRadius: 24,
        borderWidth: 1,
        gap: 14,
        marginBottom: 24,
        padding: 26,
        ...(Platform.OS === 'web' && {
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
        }),
    },
    eyebrow: {
        alignSelf: 'flex-start',
        backgroundColor: '#eef8ef',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    eyebrowText: {
        ...fonts.label,
        color: colors.primaryGreenDark,
    },
    title: {
        ...fonts.h2,
        color: colors.text900,
    },
    body: {
        ...fonts.bodyMedium,
        color: colors.text600,
        maxWidth: 720,
    },
    actions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 4,
    },
});
